'use client';

import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme, alpha } from '@mui/material/styles';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { HowItWorks, SocialProof, StickyCTA, BotInspirationFeed } from '../shared';
import { FeatureHighlights } from '../FeatureHighlights';
import { HelpChat } from '../HelpChat';
import { useScrollTracking, trackCTAClick } from '../../../lib/analytics';
import { useInspirationFeedEnabled } from '../../../lib/posthogABTesting';
import type { BotIdea } from '../content/botIdeas';
import type { ReactNode } from 'react';

/**
 * Variant B: Mobile-First Vertical
 *
 * Layout: Single-column vertical scroll optimized for mobile
 * Target: Event organizers browsing on phones, mobile social media traffic
 *
 * Features:
 * - Hero section (400px) with value prop + CTA
 * - 3-step "How It Works" visual
 * - Social proof
 * - Sticky CTA button on mobile
 *
 * Story 6.1: Marketing Landing Page with A/B Variants
 */

interface VariantBProps {
  /** The content input form as children */
  children: ReactNode;
  /** Whether value props are collapsed (user started creating) */
  collapsed?: boolean;
  /** Content to show when collapsed */
  leftContent?: ReactNode;
  /** Callback when a bot idea chip is clicked (Story 6.13) */
  onIdeaClick?: (idea: BotIdea) => void;
}

export function VariantB({
  children,
  collapsed = false,
  leftContent,
  onIdeaClick,
}: VariantBProps) {
  const t = useTranslations('creator.landing');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enabled: showInspirationFeed } = useInspirationFeedEnabled();

  // Track scroll depth for analytics (Variant B specific)
  // useScrollTracking is a plain function (not a React hook) despite its name
  useEffect(() => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const cleanup = useScrollTracking();
    return cleanup;
  }, []);

  // Use a unified layout structure to prevent children from being unmounted
  // when collapsed state changes. This preserves form state in the content input.
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 4, md: 6 },
        width: '100%',
        maxWidth: collapsed ? 'none' : 800,
        mx: collapsed ? 0 : 'auto',
      }}
    >
      {/* Collapsible Hero Section */}
      <Collapse in={!collapsed} timeout={400} unmountOnExit>
        <HeroSection />
      </Collapse>

      {/* Inspiration feed (Story 6.13) */}
      <Collapse in={!collapsed && showInspirationFeed} timeout={400} unmountOnExit>
        <BotInspirationFeed variant="horizontal" onIdeaClick={onIdeaClick} pageVariant="B" feedPosition="between-hero-and-how-it-works" />
      </Collapse>

      {/* How It Works - collapsible */}
      <Collapse in={!collapsed} timeout={400} unmountOnExit>
        <Box sx={{ px: { xs: 1, md: 0 }, bgcolor: alpha(theme.palette.primary.main, 0.08), borderRadius: 3, p: 3 }}>
          <Typography
            variant="h5"
            fontWeight={600}
            textAlign="center"
            gutterBottom
            sx={{ mb: 3 }}
          >
            {t('variants.b.howItWorksTitle')}
          </Typography>
          <HowItWorks direction={isMobile ? 'vertical' : 'horizontal'} />
        </Box>
      </Collapse>

      {/* Feature Highlights (Story 6.16) - collapsible */}
      <Collapse in={!collapsed} timeout={400} unmountOnExit>
        <FeatureHighlights variant="compact" />
      </Collapse>

      {/* Social Proof - collapsible */}
      <Collapse in={!collapsed} timeout={400} unmountOnExit>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <SocialProof variant="minimal" />
        </Box>
      </Collapse>

      {/* Help chat - collapsible */}
      <Collapse in={!collapsed} timeout={400} unmountOnExit>
        <HelpChat />
      </Collapse>

      {/* Main content area - always rendered to preserve form state */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile || !collapsed ? 'column' : 'row',
          gap: { xs: 2, md: collapsed ? 4 : 0 },
          width: '100%',
        }}
      >
        {/* Left content (sources list) on desktop when collapsed */}
        {collapsed && !isMobile && leftContent && (
          <Box
            sx={{
              flex: '0 0 380px',
              maxWidth: 380,
              position: 'sticky',
              top: 24,
              maxHeight: 'calc(100vh - 48px)',
              overflowY: 'auto',
            }}
          >
            {leftContent}
          </Box>
        )}

        {/* Content Input Section - stable wrapper to preserve form state */}
        <Box
          id="content-input"
          sx={{
            flex: 1,
            maxWidth: collapsed
              ? leftContent && !isMobile
                ? 600
                : 700
              : undefined,
            mx: collapsed && (!leftContent || isMobile) ? 'auto' : 0,
            bgcolor: 'background.paper',
            borderRadius: 3,
            p: { xs: 2, md: 4 },
            boxShadow: 1,
          }}
        >
          {/* Section heading */}
          <Typography
            variant="h4"
            fontWeight={600}
            color="primary.main"
            sx={{ mb: 1, fontSize: { xs: '1.5rem', md: '2rem' } }}
          >
            {t('contentSection.title')}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            {t('contentSection.subtitle')}
          </Typography>
          {children}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 3, textAlign: 'center' }}
          >
            {t('contentSection.helperText')}
          </Typography>
        </Box>

        {/* Note: leftContent is NOT shown on mobile - UnifiedContentPanel handles the list */}
      </Box>

      {/* Sticky CTA (mobile only, when not collapsed) */}
      {isMobile && !collapsed && (
        <StickyCTA
          threshold={400}
          targetId="content-input"
          icon="arrow"
        />
      )}
    </Box>
  );
}

/**
 * Hero Section Component
 *
 * Features:
 * - Large headline
 * - Subtitle with value proposition
 * - Primary CTA button
 * - Scroll indicator
 */
function HeroSection() {
  const t = useTranslations('creator.landing');
  const theme = useTheme();

  const handleScrollToContent = () => {
    // Track CTA click for analytics (AC6)
    trackCTAClick('create_bot', 'variant_b_hero');
    const content = document.getElementById('content-input');
    content?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        minHeight: { xs: 350, md: 400 },
        py: { xs: 4, md: 6 },
        px: 2,
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        borderRadius: 4,
      }}
    >
      {/* Headline */}
      <Typography
        variant="h3"
        component="h1"
        fontWeight={700}
        gutterBottom
        sx={{
          fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
          maxWidth: 600,
          lineHeight: 1.2,
        }}
      >
        {t('welcome.title')}
      </Typography>

      {/* Subtitle */}
      <Typography
        variant="h6"
        color="text.secondary"
        sx={{
          maxWidth: 500,
          mb: 4,
          fontWeight: 400,
          fontSize: { xs: '1rem', md: '1.125rem' },
          lineHeight: 1.5,
        }}
      >
        {t('variants.b.subtitle')}
      </Typography>

      {/* Primary CTA */}
      <Button
        variant="contained"
        size="large"
        onClick={handleScrollToContent}
        sx={{
          px: 5,
          py: 1.5,
          fontSize: '1.1rem',
          fontWeight: 600,
          borderRadius: 2,
          minWidth: 200,
          minHeight: 48, // Touch target
        }}
      >
        {t('variants.b.cta')}
      </Button>

      {/* Scroll indicator */}
      <Box
        sx={{
          mt: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          color: 'text.secondary',
          cursor: 'pointer',
          '&:hover': {
            color: 'primary.main',
          },
        }}
        onClick={handleScrollToContent}
      >
        <Typography variant="caption" sx={{ mb: 0.5 }}>
          {t('variants.b.scrollToStart')}
        </Typography>
        <ArrowDownwardIcon
          sx={{
            animation: 'bounce 2s infinite',
            '@keyframes bounce': {
              '0%, 20%, 50%, 80%, 100%': {
                transform: 'translateY(0)',
              },
              '40%': {
                transform: 'translateY(6px)',
              },
              '60%': {
                transform: 'translateY(3px)',
              },
            },
          }}
        />
      </Box>
    </Box>
  );
}
